import React, { useEffect, useRef, useState } from 'react';
import { ImagenRegistro } from '../services/imagenRegistroService';
import { imagenRegistroService } from '../services/imagenRegistroService';

interface ImageGalleryProps {
  imagenes: ImagenRegistro[];
  onImageDeleted: (imagenId: number) => void;
  onError: (message: string) => void;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ 
  imagenes, 
  onImageDeleted, 
  onError 
}) => {
  const [selectedImage, setSelectedImage] = useState<ImagenRegistro | null>(null);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [imageUrls, setImageUrls] = useState<Record<number, string>>({});
  const allocatedUrlsRef = useRef<Set<string>>(new Set());

  // Prefetch imágenes como Blob usando headers de auth del axios instance
  useEffect(() => {
    let isCancelled = false;

    const fetchMissing = async () => {
      const missing = imagenes.filter(img => !(img.id in imageUrls));
      if (missing.length === 0) return;

      const pairs = await Promise.allSettled(
        missing.map(async (img) => {
          const blob = await imagenRegistroService.downloadBlob(img.id);
          const url = URL.createObjectURL(blob);
          return [img.id, url] as const;
        })
      );

      if (isCancelled) return;
      const newMap: Record<number, string> = {};
      for (const p of pairs) {
        if (p.status === 'fulfilled') {
          const [id, url] = p.value;
          newMap[id] = url;
          allocatedUrlsRef.current.add(url);
        }
      }
      if (Object.keys(newMap).length > 0) {
        setImageUrls(prev => ({ ...prev, ...newMap }));
      }
    };

    fetchMissing();

    // Cleanup al desmontar
    return () => {
      isCancelled = true;
    };
  }, [imagenes, imageUrls]);

  // Revocar URLs cuando cambie el set de imágenes o al desmontar
  useEffect(() => {
    const allocatedAtMount = allocatedUrlsRef.current;
    return () => {
      const allocated = allocatedAtMount;
      Array.from(allocated).forEach((url) => URL.revokeObjectURL(url));
      allocated.clear();
    };
  }, []);

  const handleDelete = async (imagenId: number) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta imagen?')) {
      return;
    }

    setIsDeleting(imagenId);
    try {
      await imagenRegistroService.delete(imagenId);
      onImageDeleted(imagenId);
    } catch (error) {
      console.error('Error al eliminar imagen:', error);
      onError('Error al eliminar la imagen. Intenta nuevamente.');
    } finally {
      setIsDeleting(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (imagenes.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p>No hay imágenes para mostrar</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Imágenes del Registro</h3>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {imagenes.map((imagen) => (
          <div key={imagen.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="relative group">
              <img
                src={imageUrls[imagen.id] || imagenRegistroService.getDownloadUrl(imagen.id)}
                alt={imagen.descripcion || 'Imagen del registro'}
                loading="lazy"
                className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setSelectedImage(imagen)}
              />
              
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                <button
                  onClick={() => setSelectedImage(imagen)}
                  className="opacity-0 group-hover:opacity-100 bg-white text-gray-800 px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-100 transition-all duration-200"
                >
                  Ver
                </button>
              </div>
            </div>
            
            <div className="p-3">
              {imagen.descripcion && (
                <p className="text-sm text-gray-700 mb-2">{imagen.descripcion}</p>
              )}
              
              <div className="text-xs text-gray-500 space-y-1">
                <p>Tamaño: {formatFileSize(imagen.tamañoBytes)}</p>
                <p>Subida: {formatDate(imagen.fechaCreacion)}</p>
              </div>
              
              <div className="mt-3 flex justify-end">
                <button
                  onClick={() => handleDelete(imagen.id)}
                  disabled={isDeleting === imagen.id}
                  className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting === imagen.id ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal para vista previa */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-full overflow-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">
                {selectedImage.descripcion || 'Imagen del registro'}
              </h3>
              <button
                onClick={() => setSelectedImage(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="p-4">
              <img
                src={imageUrls[selectedImage.id] || imagenRegistroService.getDownloadUrl(selectedImage.id)}
                alt={selectedImage.descripcion || 'Imagen del registro'}
                loading="lazy"
                className="w-full h-auto max-h-96 object-contain"
              />
              
              <div className="mt-4 text-sm text-gray-600 space-y-2">
                <p><strong>Archivo:</strong> {selectedImage.nombreArchivo}</p>
                <p><strong>Tipo:</strong> {selectedImage.tipoMime}</p>
                <p><strong>Tamaño:</strong> {formatFileSize(selectedImage.tamañoBytes)}</p>
                <p><strong>Subida:</strong> {formatDate(selectedImage.fechaCreacion)}</p>
                {selectedImage.descripcion && (
                  <p><strong>Descripción:</strong> {selectedImage.descripcion}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGallery;
